import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import InventoryIcon from "@mui/icons-material/Inventory";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import Layout from "../../components/layout/Layout";
import kitchensApi from "../../api/kitchens";
import mealsApi from "../../api/meals";
import stocksApi, { StockItem, KitchenStockInfo } from "../../api/stocks";
import { nowMealsApi } from "../../api/nowMeals";
import drinksApi from "../../api/drinks";
import moment from "moment-timezone";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stock-tabpanel-${index}`}
      aria-labelledby={`stock-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const Stocks = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [kitchens, setKitchens] = useState<any[]>([]);
  const [selectedKitchenId, setSelectedKitchenId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  // Stock inventory states
  const [kitchenStocks, setKitchenStocks] = useState<KitchenStockInfo[]>([]);
  const [selectedStockInfo, setSelectedStockInfo] =
    useState<KitchenStockInfo | null>(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState<boolean>(false);
  const [adjustQty, setAdjustQty] = useState<string>("");
  const [adjustOperation, setAdjustOperation] = useState<"add" | "set">("add");

  // Meal and drink recipes states
  const [meals, setMeals] = useState<any[]>([]);
  const [nowMeals, setNowMeals] = useState<any[]>([]);
  const [drinks, setDrinks] = useState<any[]>([]);
  const [drinkSearchTerm, setDrinkSearchTerm] = useState<string>("");
  const [drinkTypeFilter, setDrinkTypeFilter] = useState<string>("all");
  const [selectedMeal, setSelectedMeal] = useState<any | null>(null);
  const [recipeItemType, setRecipeItemType] = useState<"meal" | "instantMeal" | "drink">("meal");
  const [selectedMealIsInstant, setSelectedMealIsInstant] = useState<boolean>(false);
  const [recipeDialogOpen, setRecipeDialogOpen] = useState<boolean>(false);
  const [recipePortionTab, setRecipePortionTab] = useState<"classic" | "light">("classic");
  const [editingRecipe, setEditingRecipe] = useState<
    Array<{ stockItemId: string; quantity: number }>
  >([]);
  const [editingRecipeClassic, setEditingRecipeClassic] = useState<
    Array<{ stockItemId: string; quantity: number }>
  >([]);
  const [editingRecipeLight, setEditingRecipeLight] = useState<
    Array<{ stockItemId: string; quantity: number }>
  >([]);

  // Global items states
  const [globalStocks, setGlobalStocks] = useState<StockItem[]>([]);
  const [itemDialogOpen, setItemDialogOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<StockItem | null>(null);
  const [itemFormData, setItemFormData] = useState({
    name: "",
    unit: "piece",
    description: "",
    price: "",
  });

  // Multi-select for recipe adding
  const [bulkSelectOpen, setBulkSelectOpen] = useState<boolean>(false);
  const [selectedBulkItems, setSelectedBulkItems] = useState<Set<string>>(new Set());

  const fetchKitchens = async () => {
    try {
      let res;
      try {
        res = await kitchensApi.getAccessibleKitchens();
      } catch (err) {
        res = await kitchensApi.getAdminKitchens();
      }
      const activeKitchens = (res.kitchens || []).filter(
        (k: any) => k.isActive,
      );
      setKitchens(activeKitchens);
      if (activeKitchens.length > 0) {
        setSelectedKitchenId(activeKitchens[0]._id);
      }
    } catch (err: any) {
      setError("Failed to fetch kitchens: " + (err.message || "Error"));
    }
  };

  const fetchKitchenStocks = async () => {
    if (!selectedKitchenId) return;
    setLoading(true);
    try {
      const res = await stocksApi.getKitchenStocks(selectedKitchenId);
      if (res.success) {
        setKitchenStocks(res.data);
      } else {
        setError("Failed to fetch kitchen stock levels.");
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Failed to fetch kitchen stocks.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchMeals = async () => {
    try {
      const res = await mealsApi.getMeals({ limit: 100 });
      setMeals(res.meals || []);
    } catch (err: any) {
      setError("Failed to fetch meals.");
    }
  };

  const fetchNowMeals = async () => {
    try {
      const res = await nowMealsApi.getNowMeals({ limit: 100 });
      setNowMeals(res.nowMeals || []);
    } catch (err: any) {
      setError("Failed to fetch instant meals.");
    }
  };

  const fetchDrinks = async () => {
    try {
      const res = await drinksApi.getDrinks({ limit: 100 });
      setDrinks(res.drinks || []);
    } catch (err: any) {
      setError("Failed to fetch drinks.");
    }
  };

  const fetchGlobalStocks = async () => {
    try {
      const res = await stocksApi.getStocks();
      if (res.success) {
        setGlobalStocks(res.data);
      }
    } catch (err: any) {
      setError("Failed to fetch global stock templates.");
    }
  };

  // Initial Load
  useEffect(() => {
    void fetchKitchens();
    void fetchMeals();
    void fetchNowMeals();
    void fetchDrinks();
    void fetchGlobalStocks();
  }, []);

  // Fetch kitchen specific stocks when kitchen changes
  useEffect(() => {
    if (selectedKitchenId) {
      void fetchKitchenStocks();
    }
  }, [selectedKitchenId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    setError("");
    setSuccessMsg("");
    if (newValue === 0) {
      void fetchKitchenStocks();
    } else if (newValue === 1) {
      void fetchMeals();
      void fetchGlobalStocks();
    } else if (newValue === 2) {
      void fetchDrinks();
      void fetchGlobalStocks();
    } else if (newValue === 3) {
      void fetchNowMeals();
      void fetchMeals();
      void fetchGlobalStocks();
    } else if (newValue === 4) {
      void fetchGlobalStocks();
    }
  };

  // --- Inventory Adjustments ---
  const openAdjustDialog = (stock: KitchenStockInfo, op: "add" | "set") => {
    setSelectedStockInfo(stock);
    setAdjustOperation(op);
    setAdjustQty(op === "add" ? "100" : stock.quantity.toString());
    setAdjustDialogOpen(true);
  };

  const handleAdjustStock = async () => {
    if (!selectedKitchenId || !selectedStockInfo) return;
    const qtyNum = Number(adjustQty);
    if (isNaN(qtyNum)) {
      setError("Please enter a valid number");
      return;
    }

    try {
      setError("");
      const res = await stocksApi.updateKitchenStock(selectedKitchenId, {
        stockItemId: selectedStockInfo.stockItemId,
        quantity: qtyNum,
        operation: adjustOperation,
      });

      if (res.success) {
        setSuccessMsg(
          `Successfully updated stock for ${selectedStockInfo.name}.`,
        );
        setAdjustDialogOpen(false);
        void fetchKitchenStocks();
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Failed to update kitchen stock.",
      );
    }
  };

  // --- Meal / Drink Recipe Editing ---
  const openRecipeDialog = (
    item: any,
    typeOrInstant: "meal" | "instantMeal" | "drink" | boolean = "meal"
  ) => {
    const itemType: "meal" | "instantMeal" | "drink" =
      typeof typeOrInstant === "boolean"
        ? (typeOrInstant ? "instantMeal" : "meal")
        : typeOrInstant;

    setSelectedMeal(item);
    setRecipeItemType(itemType);
    setSelectedMealIsInstant(itemType === "instantMeal");
    setRecipePortionTab("classic");

    // Map existing recipes, fallback to empty array
    const classicList = (item.stockRecipe || []).map((r: any) => ({
      stockItemId: r.stockItemId?._id || r.stockItemId,
      quantity: r.quantity,
    }));
    const lightList = (item.stockRecipeLight || []).map((r: any) => ({
      stockItemId: r.stockItemId?._id || r.stockItemId,
      quantity: r.quantity,
    }));

    setEditingRecipe(classicList);
    setEditingRecipeClassic(classicList);
    setEditingRecipeLight(lightList);
    setBulkSelectOpen(false);
    setSelectedBulkItems(new Set());
    setRecipeDialogOpen(true);
  };

  const handleSwitchRecipePortionTab = (tab: "classic" | "light") => {
    if (tab === recipePortionTab) return;
    if (recipePortionTab === "classic") {
      setEditingRecipeClassic(editingRecipe);
      setEditingRecipe(editingRecipeLight);
    } else {
      setEditingRecipeLight(editingRecipe);
      setEditingRecipe(editingRecipeClassic);
    }
    setRecipePortionTab(tab);
    setBulkSelectOpen(false);
    setSelectedBulkItems(new Set());
  };

  const handleCopyClassicRecipeToLight = () => {
    const source = recipePortionTab === "classic" ? editingRecipe : editingRecipeClassic;
    const cloned = source.map((r) => ({ ...r }));
    setEditingRecipe(cloned);
    setEditingRecipeLight(cloned);
  };

  const handleAddRecipeItem = () => {
    if (globalStocks.length === 0) return;
    // Find first stock item that isn't already in the editing recipe list
    const remaining = globalStocks.find(
      (gs) => !editingRecipe.some((er) => er.stockItemId === gs._id),
    );
    if (!remaining || !remaining._id) return;
    setEditingRecipe([
      ...editingRecipe,
      { stockItemId: remaining._id, quantity: 1 },
    ]);
  };

  // Toggle a stock item in/out of the bulk selection set
  const handleBulkToggle = (itemId: string) => {
    setSelectedBulkItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Confirm bulk selection — add items not already in recipe
  const handleConfirmBulkAdd = () => {
    const toAdd = Array.from(selectedBulkItems)
      .filter((id) => !editingRecipe.some((er) => er.stockItemId === id))
      .map((id) => ({ stockItemId: id, quantity: 1 }));
    if (toAdd.length > 0) {
      setEditingRecipe([...editingRecipe, ...toAdd]);
    }
    setBulkSelectOpen(false);
    setSelectedBulkItems(new Set());
  };

  const handleRemoveRecipeItem = (index: number) => {
    const updated = [...editingRecipe];
    updated.splice(index, 1);
    setEditingRecipe(updated);
  };

  const handleRecipeFieldChange = (
    index: number,
    field: "stockItemId" | "quantity",
    value: any,
  ) => {
    const updated = [...editingRecipe];
    if (field === "quantity") {
      updated[index].quantity = Math.max(1, Number(value) || 1);
    } else {
      updated[index].stockItemId = value;
    }
    setEditingRecipe(updated);
  };

  const handleSaveRecipe = async () => {
    if (!selectedMeal?._id) return;
    try {
      setError("");
      // Clean up recipe list to filter out duplicates or empty items
      const cleanRecipe = editingRecipe.filter(
        (item) => item.stockItemId && item.quantity > 0,
      );
      
      if (recipeItemType === "drink") {
        const res = await drinksApi.updateDrink(selectedMeal._id, {
          stockRecipe: cleanRecipe,
        });
        if (res.drink || res.success) {
          setSuccessMsg(
            `Successfully saved stock recipe for drink ${selectedMeal.name}.`,
          );
          setRecipeDialogOpen(false);
          void fetchDrinks();
        }
      } else if (recipeItemType === "instantMeal" || selectedMealIsInstant) {
        const res = await nowMealsApi.updateNowMeal(selectedMeal._id, {
          stockRecipe: cleanRecipe,
        });
        if (res.nowMeal) {
          setSuccessMsg(
            `Successfully saved stock recipe for instant meal ${selectedMeal.name}.`,
          );
          setRecipeDialogOpen(false);
          void fetchNowMeals();
        }
      } else {
        const finalClassic =
          recipePortionTab === "classic"
            ? cleanRecipe
            : editingRecipeClassic.filter((i) => i.stockItemId && i.quantity > 0);
        const finalLight =
          recipePortionTab === "light"
            ? cleanRecipe
            : editingRecipeLight.filter((i) => i.stockItemId && i.quantity > 0);

        const res = await mealsApi.patchMeal(selectedMeal._id, {
          stockRecipe: finalClassic,
          stockRecipeLight: finalLight,
        });
        if (res.meal) {
          setSuccessMsg(
            `Successfully saved stock recipe for ${selectedMeal.name}.`,
          );
          setRecipeDialogOpen(false);
          void fetchMeals();
        }
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Failed to save stock recipe.",
      );
    }
  };

  // --- Global Item Config ---
  const openItemDialog = (item: StockItem | null) => {
    setEditingItem(item);
    if (item) {
      setItemFormData({
        name: item.name,
        unit: item.unit,
        description: item.description || "",
        price: item.price != null ? String(item.price) : "",
      });
    } else {
      setItemFormData({ name: "", unit: "piece", description: "", price: "" });
    }
    setItemDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!itemFormData.name.trim()) {
      setError("Name is required");
      return;
    }
    try {
      setError("");
      const payload: Partial<StockItem> = {
        name: itemFormData.name,
        unit: itemFormData.unit,
        description: itemFormData.description,
        price: itemFormData.price !== "" ? Number(itemFormData.price) : null,
      };
      let res;
      if (editingItem?._id) {
        res = await stocksApi.updateStockItem(editingItem._id, payload);
      } else {
        res = await stocksApi.createStockItem(payload);
      }

      if (res.success) {
        setSuccessMsg(`Successfully saved stock item ${res.data.name}.`);
        setItemDialogOpen(false);
        void fetchGlobalStocks();
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Failed to save stock item template.",
      );
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this stock item? It will be removed from all kitchens and recipes.",
      )
    )
      return;
    try {
      setError("");
      const res = await stocksApi.deleteStockItem(id);
      if (res.success) {
        setSuccessMsg("Stock item template deleted successfully.");
        void fetchGlobalStocks();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to delete stock item.");
    }
  };

  const filteredDrinks = drinks.filter((d) => {
    const matchesSearch =
      !drinkSearchTerm ||
      d.name?.toLowerCase().includes(drinkSearchTerm.toLowerCase());
    const matchesType =
      drinkTypeFilter === "all" || d.type === drinkTypeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <Layout>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 3 }}
        spacing={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: "#0f172a" }}>
            Stock Management
          </Typography>
          <Typography color="text.secondary">
            Configure meal & drink recipes, global packaging/materials, and monitor
            current inventory per kitchen.
          </Typography>
        </Box>

        {tabIndex === 0 && kitchens.length > 0 && (
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="select-kitchen-label">Select Kitchen</InputLabel>
            <Select
              labelId="select-kitchen-label"
              value={selectedKitchenId}
              label="Select Kitchen"
              onChange={(e) => setSelectedKitchenId(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {kitchens.map((k) => (
                <MenuItem key={k._id} value={k._id}>
                  {k.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {tabIndex === 4 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openItemDialog(null)}
          >
            New Stock Item
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setSuccessMsg("")}
        >
          {successMsg}
        </Alert>
      )}

      <Paper
        sx={{
          width: "100%",
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "none",
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: "1px solid #e2e8f0", px: 2 }}
        >
          <Tab label="Kitchen Stock levels" />
          <Tab label="Subscription Meal Recipes" />
          <Tab label="Drink Recipes" />
          <Tab label="Instant Meal Recipes" />
          <Tab label="Global Stock Items" />
        </Tabs>

        <Box sx={{ px: 3 }}>
          {/* TAB 1: KITCHEN STOCK LEVELS */}
          <CustomTabPanel value={tabIndex} index={0}>
            {loading ? (
              <Stack direction="row" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress color="primary" />
              </Stack>
            ) : kitchenStocks.length === 0 ? (
              <Box sx={{ py: 6, textCenter: "center" }}>
                <Typography color="text.secondary" align="center">
                  No stock items defined. Go to the "Global Stock Items" tab to
                  add stock templates first.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Current Quantity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kitchenStocks.map((stock) => {
                      const isLow = stock.quantity < 100;
                      return (
                        <TableRow key={stock.stockItemId} hover>
                          <TableCell>
                            <Typography fontWeight={600} color="text.primary">
                              {stock.name}
                            </Typography>
                            {stock.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {stock.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={700}>
                              {stock.quantity.toLocaleString()} {stock.unit}(s)
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={isLow ? "Low Stock" : "In Stock"}
                              color={isLow ? "warning" : "success"}
                              sx={{ fontWeight: 600, borderRadius: 1.5 }}
                            />
                          </TableCell>
                          <TableCell color="text.secondary">
                            {stock.updatedAt
                              ? moment(stock.updatedAt)
                                  .tz("Asia/Kolkata")
                                  .format("DD MMM, hh:mm A")
                              : "-"}
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => openAdjustDialog(stock, "add")}
                              >
                                Add Stock
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<EditIcon />}
                                onClick={() => openAdjustDialog(stock, "set")}
                              >
                                Set Count
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CustomTabPanel>

          {/* TAB 2: MEAL STOCK RECIPES */}
          <CustomTabPanel value={tabIndex} index={1}>
            {meals.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
                No meals found in the rotating menu.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Meal Info</TableCell>
                      <TableCell>Packaging & Utensils Required</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {meals.map((meal) => (
                      <TableRow key={meal._id} hover>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography fontWeight={600} color="text.primary">
                            {meal.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SN: {meal.serialNumber} | Category:{" "}
                            {meal.category || "Rotating"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {/* Classic */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <Chip
                                size="small"
                                label="Classic"
                                sx={{
                                  borderRadius: 1,
                                  fontWeight: 700,
                                  fontSize: "0.68rem",
                                  backgroundColor: "#f3e8ff",
                                  color: "#6b21a8",
                                  border: "1px solid #d8b4fe",
                                }}
                              />
                              {meal.stockRecipe && meal.stockRecipe.length > 0 ? (
                                meal.stockRecipe.map((r: any) => (
                                  <Chip
                                    key={r.stockItemId?._id || r.stockItemId}
                                    size="small"
                                    label={`${r.stockItemId?.name || "Item"}: ${r.quantity}`}
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 1.5,
                                      borderColor: "#cbd5e1",
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  No classic stock mapped.
                                </Typography>
                              )}
                            </Box>

                            {/* Light */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <Chip
                                size="small"
                                label="Light"
                                sx={{
                                  borderRadius: 1,
                                  fontWeight: 700,
                                  fontSize: "0.68rem",
                                  backgroundColor: "#e0f2fe",
                                  color: "#0369a1",
                                  border: "1px solid #7dd3fc",
                                }}
                              />
                              {meal.stockRecipeLight && meal.stockRecipeLight.length > 0 ? (
                                meal.stockRecipeLight.map((r: any) => (
                                  <Chip
                                    key={r.stockItemId?._id || r.stockItemId}
                                    size="small"
                                    label={`${r.stockItemId?.name || "Item"}: ${r.quantity}`}
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 1.5,
                                      borderColor: "#7dd3fc",
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                  Using Classic recipe (fallback)
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => openRecipeDialog(meal)}
                          >
                            Configure Recipe
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CustomTabPanel>

          {/* TAB 3: DRINK STOCK RECIPES */}
          <CustomTabPanel value={tabIndex} index={2}>
            <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search drinks..."
                  value={drinkSearchTerm}
                  onChange={(e) => setDrinkSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 240 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={drinkTypeFilter}
                    label="Type"
                    onChange={(e) => setDrinkTypeFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Types ({drinks.length})</MenuItem>
                    <MenuItem value="coffee">
                      Coffee ({drinks.filter((d) => d.type === "coffee").length})
                    </MenuItem>
                    <MenuItem value="blend">
                      Blends ({drinks.filter((d) => d.type === "blend").length})
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredDrinks.length} of {drinks.length} drinks
              </Typography>
            </Box>

            {filteredDrinks.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
                No drinks found.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Drink Info</TableCell>
                      <TableCell>Category & Tier</TableCell>
                      <TableCell>Packaging & Stock Required</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDrinks.map((drink) => {
                      const hasStockRecipe =
                        Array.isArray(drink.stockRecipe) && drink.stockRecipe.length > 0;

                      return (
                        <TableRow key={drink._id} hover>
                          <TableCell sx={{ minWidth: 200 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              {drink.imageUrl && (
                                <Box
                                  component="img"
                                  src={drink.imageUrl}
                                  alt={drink.name}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 1.5,
                                    objectFit: "cover",
                                    border: "1px solid #e2e8f0",
                                  }}
                                />
                              )}
                              <Box>
                                <Typography fontWeight={600} color="text.primary">
                                  {drink.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {drink.volumeMl ? `${drink.volumeMl}ml` : "300ml"} |{" "}
                                  {drink.nutritionalInfo?.calories || 0} kcal
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={
                                drink.type === "coffee" ? (
                                  <LocalCafeIcon sx={{ fontSize: "0.9rem !important" }} />
                                ) : (
                                  <LocalBarIcon sx={{ fontSize: "0.9rem !important" }} />
                                )
                              }
                              label={drink.type === "coffee" ? "Coffee" : "Blend"}
                              sx={{
                                borderRadius: 1.5,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                textTransform: "capitalize",
                                backgroundColor:
                                  drink.type === "coffee" ? "#fef3c7" : "#ecfdf5",
                                color:
                                  drink.type === "coffee" ? "#92400e" : "#065f46",
                                border:
                                  drink.type === "coffee"
                                    ? "1px solid #fde68a"
                                    : "1px solid #a7f3d0",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {hasStockRecipe ? (
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {drink.stockRecipe.map((r: any) => (
                                  <Chip
                                    key={r.stockItemId?._id || r.stockItemId}
                                    size="small"
                                    label={`${r.stockItemId?.name || "Item"}: ${r.quantity}`}
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 1.5,
                                      borderColor: "#cbd5e1",
                                      fontWeight: 500,
                                    }}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                No packaging/stock requirements mapped.
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => openRecipeDialog(drink, "drink")}
                            >
                              Configure Recipe
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CustomTabPanel>

          {/* TAB 4: INSTANT MEAL STOCK RECIPES */}
          <CustomTabPanel value={tabIndex} index={3}>
            {nowMeals.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
                No instant meals found in the menu.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Instant Meal Info</TableCell>
                      <TableCell>Reference Subscription Meal</TableCell>
                      <TableCell>Packaging & Utensils Required (Overridden)</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nowMeals.map((meal) => {
                      const refMeal = meals.find((sm: any) => sm._id === meal.refMealId);
                      const hasCustomRecipe = meal.stockRecipe && meal.stockRecipe.length > 0;
                      
                      return (
                        <TableRow key={meal._id} hover>
                          <TableCell sx={{ minWidth: 200 }}>
                            <Typography fontWeight={600} color="text.primary">
                              {meal.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Category: {meal.categoryId?.name || "Uncategorized"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {refMeal ? (
                              <Typography variant="body2" color="text.primary">
                                {refMeal.name}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                Created from scratch
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {hasCustomRecipe ? (
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {meal.stockRecipe.map((r: any) => (
                                  <Chip
                                    key={r.stockItemId?._id || r.stockItemId}
                                    size="small"
                                    label={`${r.stockItemId?.name || "Item"}: ${r.quantity}`}
                                    variant="outlined"
                                    sx={{ borderRadius: 1.5, borderColor: "#10b981", color: "#047857", backgroundColor: "#ecfdf5" }}
                                  />
                                ))}
                              </Stack>
                            ) : meal.refMealId ? (
                              <Typography variant="caption" color="text.secondary">
                                Using reference meal's recipe (automatic fallback)
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="error">
                                No recipe configuration. Click configure to add.
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => openRecipeDialog(meal, "instantMeal")}
                            >
                              Configure Recipe
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CustomTabPanel>

          {/* TAB 5: GLOBAL STOCK CONFIG */}
          <CustomTabPanel value={tabIndex} index={4}>
            {globalStocks.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
                No global stock items configured yet. Click "New Stock Item"
                above to get started.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Measurement Unit</TableCell>
                      <TableCell>Price (₹ / unit)</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {globalStocks.map((item) => (
                      <TableRow key={item._id} hover>
                        <TableCell>
                          <Typography fontWeight={600} color="text.primary">
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={item.unit}
                            sx={{ borderRadius: 1.5 }}
                          />
                        </TableCell>
                        <TableCell>
                          {item.price != null ? (
                            <Typography fontWeight={700} color="success.dark" fontSize="0.85rem">
                              ₹{item.price.toLocaleString("en-IN")}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell color="text.secondary">
                          {item.description || "-"}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openItemDialog(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                item._id && handleDeleteItem(item._id)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CustomTabPanel>
        </Box>
      </Paper>

      {/* DIALOG 1: ADJUST KITCHEN STOCK LEVEL */}
      <Dialog
        open={adjustDialogOpen}
        onClose={() => setAdjustDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {adjustOperation === "add"
            ? "Log Incoming Delivery"
            : "Adjust Stock Overwrite"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {adjustOperation === "add"
                ? `Adds quantity to ${selectedStockInfo?.name}'s current balance (${selectedStockInfo?.quantity}).`
                : `Sets ${selectedStockInfo?.name}'s count to a new absolute value.`}
            </Typography>
            <TextField
              label="Quantity"
              type="number"
              value={adjustQty}
              onChange={(e) => setAdjustQty(e.target.value)}
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAdjustStock} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG 2: EDIT MEAL STOCK RECIPE */}
      <Dialog
        open={recipeDialogOpen}
        onClose={() => setRecipeDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="h6" component="span" fontWeight={700}>
              Stock Requirements: {selectedMeal?.name}
            </Typography>
            {recipeItemType === "meal" && !selectedMealIsInstant && (
              <Box sx={{ display: "inline-flex", p: 0.5, bgcolor: "#f1f5f9", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Button
                  size="small"
                  variant={recipePortionTab === "classic" ? "contained" : "text"}
                  onClick={() => handleSwitchRecipePortionTab("classic")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    borderRadius: 1.5,
                    bgcolor: recipePortionTab === "classic" ? "#7c3aed" : "transparent",
                    color: recipePortionTab === "classic" ? "#fff" : "#475569",
                    "&:hover": { bgcolor: recipePortionTab === "classic" ? "#6d28d9" : "#e2e8f0" },
                  }}
                >
                  Classic ({(recipePortionTab === "classic" ? editingRecipe : editingRecipeClassic).length})
                </Button>
                <Button
                  size="small"
                  variant={recipePortionTab === "light" ? "contained" : "text"}
                  onClick={() => handleSwitchRecipePortionTab("light")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    borderRadius: 1.5,
                    bgcolor: recipePortionTab === "light" ? "#0284c7" : "transparent",
                    color: recipePortionTab === "light" ? "#fff" : "#475569",
                    "&:hover": { bgcolor: recipePortionTab === "light" ? "#0369a1" : "#e2e8f0" },
                  }}
                >
                  Light ({(recipePortionTab === "light" ? editingRecipe : editingRecipeLight).length})
                </Button>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {recipeItemType === "drink"
                ? "Specify packaging, bottles, stickers, or stock items that decrement when this drink is delivered."
                : selectedMealIsInstant
                ? "Specify stock items that decrement when this instant meal is delivered."
                : `Specify ${recipePortionTab === "classic" ? "Classic" : "Light"} portion stock items decrementing when delivered.`}
            </Typography>

            {recipeItemType === "meal" && !selectedMealIsInstant && recipePortionTab === "light" && editingRecipe.length === 0 && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Typography variant="caption" color="#0369a1" fontWeight={600}>
                  No separate Light recipe mapped. Delivery will automatically fall back to the Classic recipe.
                </Typography>
                {editingRecipeClassic.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon sx={{ fontSize: "1rem" }} />}
                    onClick={handleCopyClassicRecipeToLight}
                    sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 700, borderColor: "#7dd3fc", color: "#0369a1" }}
                  >
                    Copy from Classic
                  </Button>
                )}
              </Box>
            )}

            {editingRecipe.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                No stock requirements mapped yet for this portion. Click "Add Requirements" to configure.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {editingRecipe.map((recipeItem, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>Stock Item</InputLabel>
                      <Select
                        value={recipeItem.stockItemId}
                        label="Stock Item"
                        onChange={(e) =>
                          handleRecipeFieldChange(
                            idx,
                            "stockItemId",
                            e.target.value,
                          )
                        }
                      >
                        {globalStocks.map((gs) => (
                          <MenuItem
                            key={gs._id}
                            value={gs._id}
                            disabled={editingRecipe.some(
                              (er, erIdx) =>
                                er.stockItemId === gs._id && erIdx !== idx,
                            )}
                          >
                            {gs.name} ({gs.unit})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Qty Per Meal"
                      type="number"
                      size="small"
                      value={recipeItem.quantity}
                      onChange={(e) =>
                        handleRecipeFieldChange(idx, "quantity", e.target.value)
                      }
                      sx={{ width: 150 }}
                      inputProps={{ min: 1 }}
                    />

                    <IconButton
                      color="error"
                      onClick={() => handleRemoveRecipeItem(idx)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}

            {/* Multi-select panel */}
            <Box sx={{ mt: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    // Pre-tick items already in recipe
                    const preSelected = new Set(
                      editingRecipe.map((r) => r.stockItemId)
                    );
                    setSelectedBulkItems(preSelected);
                    setBulkSelectOpen((v) => !v);
                  }}
                  disabled={editingRecipe.length >= globalStocks.length}
                >
                  {bulkSelectOpen ? "Hide Selector" : "Add Requirements"}
                </Button>
                {bulkSelectOpen && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleConfirmBulkAdd}
                  >
                    Confirm Selection
                  </Button>
                )}
              </Stack>

              <Collapse in={bulkSelectOpen}>
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    maxHeight: 260,
                    overflowY: "auto",
                    borderColor: "#e2e8f0",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Select stock items to include in this recipe. Already-added items are pre-checked.
                  </Typography>
                  <Stack spacing={0.5}>
                    {globalStocks.map((gs) => (
                      <FormControlLabel
                        key={gs._id}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedBulkItems.has(gs._id!)}
                            onChange={() => handleBulkToggle(gs._id!)}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {gs.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={gs.unit}
                              sx={{ borderRadius: 1, height: 18, fontSize: "0.65rem" }}
                            />
                            {gs.price != null && (
                              <Typography variant="caption" color="success.dark" fontWeight={700}>
                                ₹{gs.price}
                              </Typography>
                            )}
                            {editingRecipe.some(
                              (er) => er.stockItemId === gs._id && !selectedBulkItems.has(gs._id!)
                            ) && (
                              <Chip
                                size="small"
                                label="already added"
                                color="warning"
                                sx={{ borderRadius: 1, height: 18, fontSize: "0.65rem" }}
                              />
                            )}
                          </Box>
                        }
                        sx={{ mx: 0, py: 0.5, borderBottom: "1px solid #f1f5f9" }}
                      />
                    ))}
                  </Stack>
                </Paper>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveRecipe} variant="contained">
            Save Recipe
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG 3: CREATE / EDIT GLOBAL STOCK ITEM */}
      <Dialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingItem ? "Edit Stock Item Template" : "New Global Stock Item"}
        </DialogTitle>
        <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Item Name"
              value={itemFormData.name}
              onChange={(e) =>
                setItemFormData({ ...itemFormData, name: e.target.value })
              }
              fullWidth
              autoFocus
            />
            <Autocomplete
              freeSolo
              options={[
                "piece",
                "box",
                "packet",
                "bag",
                "roll",
                "tray",
                "sheet",
                "bottle",
                "can",
                "g",
                "kg",
                "ml",
                "L",
                "serving",
              ]}
              value={itemFormData.unit}
              onInputChange={(_e, newValue) =>
                setItemFormData({ ...itemFormData, unit: newValue })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Measurement Unit"
                  helperText="Pick a common unit or type a custom one (e.g. tray, roll, bottle)"
                />
              )}
            />
            <TextField
              label="Price per Unit (₹) — Optional"
              type="number"
              value={itemFormData.price}
              onChange={(e) =>
                setItemFormData({ ...itemFormData, price: e.target.value })
              }
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="For procurement cost records only. Leave blank if not applicable."
            />
            <TextField
              label="Description (Optional)"
              value={itemFormData.description}
              onChange={(e) =>
                setItemFormData({
                  ...itemFormData,
                  description: e.target.value,
                })
              }
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveItem} variant="contained">
            Save Item
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Stocks;
